import type { ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { SurfaceCard } from '@/design/SurfaceCard';
import { adoptionLevelCounts } from './AdoptionBands';
import type { AdoptionBreakdownRow } from './AdoptionTypes';

/**
 * How the org's users spread across the adoption maturity bands, plus the Inactive
 * segment (in the directory, no activity in the window). Rendered as labeled bars —
 * the hero gauge is already the page's ring, so this deliberately isn't a donut.
 */
export default function AdoptionLevelDistribution({ rows }: { rows: AdoptionBreakdownRow[] }): ReactElement {
    const segments = adoptionLevelCounts(rows);
    const total = segments.reduce((sum, segment) => sum + segment.count, 0);
    const max = Math.max(1, ...segments.map((segment) => segment.count));

    return (
        <SurfaceCard>
            <Text fw={700} style={{ fontSize: 15 }} mb={2}>
                Adoption by level
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
                {total.toLocaleString()} users in scope
            </Text>
            <Stack gap={10}>
                {segments.map((segment) => {
                    const pct = total === 0 ? 0 : Math.round((segment.count / total) * 100);
                    return (
                        <Box key={segment.name}>
                            <Group justify="space-between" mb={4} wrap="nowrap">
                                <Group gap={7} wrap="nowrap" align="center">
                                    <Box style={{ width: 9, height: 9, borderRadius: 2, background: segment.color, flexShrink: 0 }} />
                                    <Text size="sm">{segment.name}</Text>
                                </Group>
                                <Group gap={6} wrap="nowrap" align="baseline">
                                    <Text fw={700} style={{ fontSize: 14 }}>
                                        {segment.count.toLocaleString()}
                                    </Text>
                                    <Text c="dimmed" style={{ fontSize: 11 }}>
                                        {pct}%
                                    </Text>
                                </Group>
                            </Group>
                            <Box style={{ height: 8, background: 'var(--bm-color-surface-alt)', borderRadius: 999 }}>
                                <Box style={{ height: '100%', width: `${(segment.count / max) * 100}%`, minWidth: segment.count > 0 ? 4 : 0, borderRadius: 999, background: segment.color }} />
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </SurfaceCard>
    );
}
